import { useEffect, useState } from 'react';
import moment from 'moment';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import { DataTable } from '../../components/nextgen';

import useAsync from '../../hooks/useAsync';

import { getExpiringContractsAdmin } from '../../api/admin';

const ExpiringContracts = () => {
	const [page, setPage] = useState(1);
	const [days, setDays] = useState(90);

	const {
		value: contractsData,
		execute: refreshContracts,
		status: loadingStatus,
	} = useAsync(getExpiringContractsAdmin, { immediate: false });

	useEffect(() => {
		refreshContracts({ page: page - 1, limit: 25, days });
	}, [page, days]);

	const isLoading = loadingStatus === 'pending';

	const getDaysRemaining = (endDate) => {
		const end = moment(endDate).startOf('day');
		const today = moment().startOf('day');
		return end.diff(today, 'days');
	};

	const columns = [
		{
			key: 'name',
			header: 'Contract',
			sortable: false,
			render: (row) => (
				<div>
					<div className="text-gray-900 font-medium">{row.name || '-'}</div>
					{row.category && (
						<div className="text-gray-500 text-xs">{row.category.name}</div>
					)}
				</div>
			),
		},
		{
			key: 'organization',
			header: 'Organization',
			width: '200px',
			sortable: false,
			render: (row) => (
				<div className="text-gray-900">{row.organization?.name || '-'}</div>
			),
		},
		{
			key: 'vendor',
			header: 'Vendor',
			width: '180px',
			sortable: false,
			render: (row) => (
				<div className="text-gray-700">{row.vendor?.name || '-'}</div>
			),
		},
		{
			key: 'assets',
			header: 'Assets',
			width: '100px',
			sortable: false,
			render: (row) => (
				<span className="text-gray-700">{row.assetsCount || 0}</span>
			),
		},
		{
			key: 'endDate',
			header: 'Expires',
			width: '180px',
			sortable: false,
			render: (row) => {
				const daysRemaining = getDaysRemaining(row.endDate);
				const isUrgent = daysRemaining <= 7;
				const isWarning = daysRemaining <= 14 && daysRemaining > 7;

				return (
					<div>
						<div className="text-gray-900">
							{moment(row.endDate).format('MMM D, YYYY')}
						</div>
						<div
							className={`text-xs font-medium ${
								isUrgent
									? 'text-red-600'
									: isWarning
										? 'text-amber-600'
										: 'text-gray-500'
							}`}
						>
							{daysRemaining === 0
								? 'Expires today'
								: daysRemaining === 1
									? '1 day left'
									: `${daysRemaining} days left`}
						</div>
					</div>
				);
			},
		},
	];

	const filterOptions = [
		{ label: '7 days', value: 7 },
		{ label: '14 days', value: 14 },
		{ label: '30 days', value: 30 },
		{ label: '60 days', value: 60 },
		{ label: '90 days', value: 90 },
	];

	return (
		<MainContent>
			<div className="flex items-center justify-between mb-4">
				<PageHeading divider={false}>Expiring Contracts</PageHeading>
				<div className="flex items-center gap-2">
					<span className="text-sm text-gray-600">Show contracts expiring in:</span>
					<select
						value={days}
						onChange={(e) => {
							setDays(Number(e.target.value));
							setPage(1);
						}}
						className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
					>
						{filterOptions.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</div>
			</div>

			<div className="border border-gray-200 rounded-lg overflow-hidden">
				<DataTable
					data={contractsData?.rows || []}
					columns={columns}
					loading={isLoading}
					pagination
					serverSidePagination
					currentPage={page}
					totalItems={contractsData?.count || 0}
					onPageChange={setPage}
					pageSize={25}
					searchable
					searchPlaceholder="Search contracts..."
					searchFields={['name', 'organization.name', 'vendor.name']}
					emptyTitle="No expiring contracts found"
					emptyDescription={`There are no contracts expiring within the next ${days} days`}
					hoverable
					bordered={false}
				/>
			</div>
		</MainContent>
	);
};

export default ExpiringContracts;
